import React, {useState, useCallback, useEffect} from 'react';
import DialogComponent from 'components/Dialog';
import {DataGridPro, GridColDef, GridValueGetterParams} from '@mui/x-data-grid-pro';

import {Trans} from 'react-i18next';

import {DialogTypes} from '../../../../../common/names';
import i18n from '../../../../../i18n';
import Button from '../../../../../components/Button';
import AddEditCalendarDialog from './AddEditCalendar';
import {HolidayCalendar, IHolidayCalendar} from '../../../../../types/HolidayCalendar';
import ViewCalendarDialog from './ViewCalendar/ViewCalendar';
import {useSelector, useDispatch} from 'react-redux';
import {layoutSelectors, layoutActions} from '../../../redux';
import ConfirmationWrapper from '../../../../../components/buttons/ConfirmationWrapper';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {WarningButton} from 'components/buttons/WarningButton';
import {maxBy} from 'lodash';
import {IHoliday} from 'types/Holiday';
import {Grid} from '@mui/material';
import {castToSend} from 'common/yup/castToSend';
import {formatDate} from 'common/dateUtils';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';

const cols: GridColDef[] = [
   {field: 'name', headerName: i18n.t('Calendar Name'), flex: 200},
   {field: 'description', headerName: i18n.t('Description'), flex: 300},
   {
      field: 'latestDate',
      headerName: i18n.t('Latest Date Scheduled'),
      flex: 200,
      valueGetter: (params: GridValueGetterParams) => {
         const holidays = (params.row.holidays || []) as IHoliday[];
         const maxVal = maxBy(holidays, (o: IHoliday) => new Date(o.date || ''));
         return formatDate(maxVal?.date as Date);
      },
   },
];

type CalendarsDialogProps = {
   onClose: () => void;
};
const CalendarsDialog: ReactFChildrenProps<CalendarsDialogProps> = (props) => {
   const {onClose} = props;
   const [selected, setSelected] = useState<IHolidayCalendar | null>();
   const [isAddOpened, addOpen] = useState(false);
   const [isEditOpened, editOpen] = useState(false);
   const [isViewOpened, viewOpen] = useState(false);

   const dispatch = useDispatch();
   const calendars = useSelector(layoutSelectors.getCalendars);
   // console.debug(calendars);

   const loadCalendars = React.useCallback(() => {
      dispatch(layoutActions.loadCalendars());
      setSelected(null);
   }, [dispatch]);

   useEffect(() => {
      loadCalendars();
   }, [loadCalendars]);

   const add = useCallback(
      (calendar: IHolidayCalendar, formikBag: FormOnSubmitHelpers<IHolidayCalendar>) =>
         dispatch(
            layoutActions.addCalendar(castToSend(HolidayCalendar, calendar), {
               onSuccess: () => {
                  loadCalendars();
                  addOpen(false);
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, addOpen, loadCalendars]
   );
   const edit = useCallback(
      (calendar: IHolidayCalendar, formikBag: FormOnSubmitHelpers<IHolidayCalendar>) =>
         dispatch(
            layoutActions.editCalendar(castToSend(HolidayCalendar, calendar), {
               onSuccess: () => {
                  loadCalendars();
                  editOpen(false);
               },
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, editOpen, loadCalendars]
   );
   const deleteCalendar = useCallback(
      (id: string) =>
         dispatch(
            layoutActions.deleteCalendar(id, {
               onSuccess: loadCalendars,
            })
         ),
      [dispatch, loadCalendars]
   );

   return (
      <DialogComponent maxWidth="md" header={DialogTypes.Calendars.dialogName} close={onClose}>
         <div style={{height: 400, width: '100%'}}>
            <DataGridPro
               disableMultipleSelection
               density="compact"
               rows={calendars}
               columns={cols}
               pageSize={8}
               onSelectionModelChange={(selectionModel) =>
                  setSelected(calendars.find((c) => c.id === selectionModel[0]) as IHolidayCalendar)
               }
            />
         </div>

         <Grid container spacing={3} item md={8}>
            <Grid item>
               <PrimaryButton onClick={() => addOpen(true)}>
                  <Trans>Add</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <ConfirmationWrapper
                  onOk={() => selected && deleteCalendar(selected.id)}
                  disabled={!selected}
               >
                  <Button color="secondary" disabled={!selected}>
                     <Trans>Delete</Trans>
                  </Button>
               </ConfirmationWrapper>
            </Grid>
            <Grid item>
               <PrimaryButton onClick={() => editOpen(true)} disabled={!selected}>
                  <Trans>Edit</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <WarningButton onClick={() => viewOpen(true)} disabled={!selected}>
                  <Trans>View</Trans>
               </WarningButton>
            </Grid>
         </Grid>
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <Button type="button" onClick={onClose}>
                  <Trans>Close</Trans>
               </Button>
            </Grid>
         </Grid>
         {isAddOpened ? (
            <AddEditCalendarDialog onSubmit={add} onClose={() => addOpen(false)} />
         ) : null}

         {isEditOpened ? (
            <AddEditCalendarDialog
               calendar={selected!}
               onSubmit={edit}
               onClose={() => editOpen(false)}
            />
         ) : null}

         {isViewOpened ? (
            <ViewCalendarDialog calendar={selected} onClose={() => viewOpen(false)} />
         ) : null}
      </DialogComponent>
   );
};

export default React.memo(CalendarsDialog);
